import React, { useState, useEffect } from 'react';
import { 
  Container,
  Typography,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Box,
} from '@mui/material';
import { styled } from '@mui/system';

import AFRICAN_COUNTRIES from '../helpers/African_countries';
import Feedback from '../components/Feedback';
import { collection, getDocs, limit, orderBy, query, where } from 'firebase/firestore';

import { db } from '../firebase/firebase-config';

const CURRENCIES = ["USDT", "USDC"];

const StyledContainer = styled(Container)(({ theme }) => ({
  height: '100vh',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
//   padding: theme.spacing(3),
  padding: "1em",
  position: 'relative',
}));

const RateText = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1.5rem, 6vw, 4rem)',
  fontWeight: 'bold',
//   margin: theme.spacing(4),
  margin: "1em",
}));


async function detectCountry() {
  try {
    const response = await fetch(`https://ipapi.co/json/`);
    const data = await response.json();
    return AFRICAN_COUNTRIES.find(c => c.code === data.country) || AFRICAN_COUNTRIES[0];
  } catch {
    return AFRICAN_COUNTRIES[0];
  }
}


const Home = () => {

    const [selectedCountry, setSelectedCountry] = useState("");
    const [selectedCurrency, setSelectedCurrency] = useState("");
    const [rate, setRate] = useState(null);
    const [date, setDate] = useState(null)
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);  // State for manual override

    const [unsubscribe, setUnsubscribe] = useState(null);
    
    // 1. Modify the initialization useEffect to check localStorage first
    useEffect(() => {
        const initializeCountry = async () => {
        // Check localStorage for existing selection
        const storedCountry = JSON.parse(localStorage.getItem('selectedCountry'));
        
        if (storedCountry) {
            setSelectedCountry(storedCountry);
            return;
        }

        // If no stored country, detect from IP
        try {
            const defaultCountry = await detectCountry();
            setSelectedCountry(defaultCountry);
        } catch {
            setSelectedCountry(AFRICAN_COUNTRIES[0]); // Fallback to Ethiopia
        }
        };
        initializeCountry();
    }, []);

    useEffect(() => {
        const initializeCurrency = () => {
        // check localstorage for existing currency
        const storedCurrency = JSON.parse(localStorage.getItem("selectedCurrency"));

        if(storedCurrency){
            setSelectedCurrency(storedCurrency);
        }
        else{
            setSelectedCurrency("USDT");
        }
        };
        initializeCurrency();
    }, []);

    // 2. Add a useEffect to save to localStorage when country changes
    useEffect(() => {
        if (selectedCountry) {
        localStorage.setItem('selectedCountry', JSON.stringify(selectedCountry));
        }
        
    }, [selectedCountry]); // Runs whenever selectedCountry changes

    useEffect(() => {
        if(selectedCurrency){
        localStorage.setItem("selectedCurrency", JSON.stringify(selectedCurrency));
        }
    }, [selectedCurrency]);

    useEffect(() => {
        if(!selectedCountry) return;
        if(!selectedCurrency) return;

        const fetchRate = async() => {
            // we unsubscribe to the firestore listener
            if(unsubscribe) unsubscribe();

            try{
                setLoading(true);

                const currencyRef = collection(db, selectedCountry.currency)
                const q = query(
                    currencyRef,
                    where("asset", "==", selectedCurrency),
                    orderBy("createdAt", "desc"),
                    limit(1)
                );

                const snapshot = await getDocs(q);
                const data = [];
                snapshot.forEach((doc) => 
                    data.push({ id: doc.id, ...doc.data() })
                );

                setRate(data[0].rate);
                setDate(new Date(data[0].createdAt.seconds * 1000));
            }
            catch(e){
                console.log("error fetching data: ", e);
            }
            finally{
                setLoading(false);
            }
            
        }

        fetchRate();


    }, [selectedCountry, selectedCurrency]);


    // useEffect(() => {
    //     if(!selectedCountry) return;
    //     if(!selectedCurrency) return;

    //     const fetchRate = async () => {
    //     try {
    //         setLoading(true);
    //         const rate = await fetchExchangeRate(selectedCountry.currency, selectedCurrency);
    //         setRate(rate);
    //         setError(null);
    //     } catch (err) {
    //         setError(err.message);
    //     } finally {
    //         setLoading(false);
    //     }
    //     };

    //     fetchRate();

    //     const interval = setInterval(fetchRate, 60 * 1 * 1000);
    //     return () => clearInterval(interval);
    // }, [selectedCountry, selectedCurrency]);

    // Title update useEffect
    
    useEffect(() => {
        if (selectedCountry) {
        document.title = `Afri-Rate | ${selectedCountry.name}`;
        // For a more polished look, you might want:
        // document.title = `${selectedCountry.currency} Exchange Rate | AfriRate`;
        }
    }, [selectedCountry]);



    const handleCountrySelect = (e) => {
        // google analytics stuff
        // window.gtag('event', 'click', {
        // event_category: 'Select',
        // event_label: 'Select Country',
        // });

        const country = AFRICAN_COUNTRIES.find(c => c.code === e.target.value)
        setSelectedCountry(country);
    };

    const handleCurrencySelect = (e) => {
        // google analytics stuff
        // window.gtag('event', 'click', {
        // event_category: 'Select',
        // event_label: 'Select Currency',
        // });

        setSelectedCurrency(e.target.value);
    };

    return (
        <StyledContainer maxWidth="md" style={{height: "38vh"}}>
            <Box sx={{ position: 'absolute', top: 16, right: { md: 48, lg: 32, sm: 64 } }}>
            <Select
                value={selectedCountry?.code || ''}
                onChange={handleCountrySelect}
                size="small"
            >
                {AFRICAN_COUNTRIES.map((country) => (
                <MenuItem key={country.code} value={country.code}>
                    {country.name}
                </MenuItem>
                ))}
            </Select>


            </Box>
            <Box sx={{ position: 'absolute', left: 16, top: 16 }}>
            <Select
                value={selectedCurrency}
                onChange={handleCurrencySelect}
                size="small"
            >
                {CURRENCIES.map((currency) => (
                <MenuItem key={currency} value={currency}>
                    {currency}
                </MenuItem>
                ))}
            </Select>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
            <CircularProgress size={60} />
            ) : (
            <RateText>
                1 {selectedCurrency} ≈ {rate} {selectedCountry?.currency}
                <span style={{fontSize: "14px", display: "block", color: "#aaa"}}>{date ? date.toString().slice(0, 15) : ""}</span>
            </RateText>
            )}

            <Feedback />
        </StyledContainer>
    )
}

export default Home;